import React, { useCallback, useEffect, useState } from "react";
import S from "./strings";
import Evaluation from "./Evaluation";
import Questionnaire from "./Questionnaire";
import { isRtlLanguage, monthYear } from "./locale";
import useUiLanguage from "./useUiLanguage";
import { coachingPersonalityService } from "../../../services/coachingPersonalityApi";

// COACHING PERSONALITY — the owner's section, on both profile types.
//
// Konzept 4.1 makes this section the home of the whole feature: the empty state
// with its Start, the questionnaire, the evaluation with the release switch.
// Nothing of it lives on a route of its own and nothing of it leaks anywhere
// else. It REPLACES the "PERSONALITY TEST . COMING SOON" slot that already sat
// at the bottom of the staff profile, and is a new block at the bottom of the
// coach profile, which never had that slot.
//
// FOUR STATES, NOT THREE (coaching_personality_views.py:150):
//   empty      nothing yet          -> the hint and Start
//   draft      a run in flight      -> resume or discard, no result exists
//   retaking   a run in flight AND a result still in force -> BOTH are drawn,
//              because Konzept 3.4 keeps the old result valid "until new
//              answers are given"
//   completed  a result             -> the evaluation
//
// ABSENCE RENDERS NOTHING. A person the section does not apply to (an agent, a
// player, a lawyer) gets a 404 from every endpoint, and this returns null: no
// heading, no empty box, no error. The same is true while the first request is
// in flight, so the profile never flashes a frame that then disappears.

const PILL_NEUTRAL =
  "text-[11px] text-[#6B7280] bg-white border border-[#E5E7EB] rounded-full px-[10px] py-1";

const CoachingPersonalitySection = () => {
  const language = useUiLanguage();
  const rtl = isRtlLanguage(language);

  const [loaded, setLoaded] = useState(false);
  const [applies, setApplies] = useState(false);
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [startOnIntro, setStartOnIntro] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    const response = await coachingPersonalityService.getHistory();
    setHistory(response.success ? response.data?.results || [] : []);
  }, []);

  const load = useCallback(async () => {
    const response = await coachingPersonalityService.getState();
    setLoaded(true);
    if (!response.success) {
      // 404 means the section does not apply to this person at all; any other
      // failure is not something an optional profile block should shout about.
      setApplies(false);
      return;
    }
    setApplies(true);
    setState(response.data);
    if (response.data?.result) loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    load();
  }, [load]);

  if (!loaded || !applies || !state) return null;

  const result = state.result || null;
  const draft = state.draft || null;
  const lock = state.lock || null;
  const canStart = lock ? !!lock.can_retake : true;
  const lockDate = lock?.retake_available_at
    ? monthYear(lock.retake_available_at, language)
    : "";

  const beginRun = async () => {
    setBusy(true);
    const response = await coachingPersonalityService.start();
    setBusy(false);
    if (!response.success) {
      // The lock refused (409). The section already states when the next run is
      // possible, so the refusal needs no second voice; the state is refreshed
      // in case it moved on somewhere else.
      load();
      return;
    }
    setStartOnIntro(true);
    setQuestionnaire(response.data);
  };

  const resumeRun = async () => {
    setBusy(true);
    const response = await coachingPersonalityService.getQuestionnaire();
    setBusy(false);
    if (!response.success) {
      load();
      return;
    }
    setStartOnIntro(false);
    setQuestionnaire(response.data);
  };

  const discardRun = async () => {
    setBusy(true);
    await coachingPersonalityService.discardDraft();
    setBusy(false);
    load();
  };

  const leaveRun = () => {
    setQuestionnaire(null);
    load();
  };

  const shell = (children) => (
    <div className="bg-white p-6 border border-[#E6E6E6]" dir={rtl ? "rtl" : undefined}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* The neighbouring sections write their heading in capitals as
            literal text. Here the capitals are CSS: the registered key is
            "Coaching Personality", and a node reading "COACHING PERSONALITY"
            would match nothing and stay English in sixteen languages. */}
        {/* min-width, not width (layout rule 1): the three pills next to it
            are much wider in German than in English, and with `min-w-0` the
            heading was squeezed to about thirty pixels at 375px and cut off
            mid-word. A minimum makes the flex line break instead, so the pills
            move to their own row and the heading always reads in full.
            Measured, not assumed: probe run 2026-08-03, de/hr/fr/ar at 375. */}
        <h2 className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#3C3C3C] flex-1 min-w-[140px]">
          {S.sectionTitle}
        </h2>
        {result && (
          <>
            <span className={PILL_NEUTRAL}>{S.tagSelfAssessment}</span>
            <span className={PILL_NEUTRAL}>{S.tagBigFive}</span>
            {/* Claimed only while it is true. The prototype shows this badge
                on every result; once the switch is on, a pitched club can see
                the card, so the sentence would be a false promise. */}
            {!result.is_released && (
              <span className="text-[11px] font-semibold text-[#2183FE] bg-[#E9F2FF] border border-[#BBD8FE] rounded-full px-[10px] py-1">
                {S.badgePrivate}
              </span>
            )}
          </>
        )}
      </div>
      {children}
    </div>
  );

  if (questionnaire) {
    return shell(
      <Questionnaire
        questionnaire={questionnaire}
        startOnIntro={startOnIntro}
        onPause={leaveRun}
        onLeaveIntro={leaveRun}
        onCompleted={() => {
          setQuestionnaire(null);
          load();
        }}
      />
    );
  }

  const resumeCard = draft ? (
    <div className="bg-[#E9F2FF] rounded-[8px] mt-4 py-[clamp(24px,4vw,40px)] px-[clamp(16px,3vw,32px)] flex flex-col items-center text-center">
      <div className="text-[16px] font-bold text-[#111827]">{S.resumeTitle}</div>
      {/* One node: number and sentence are a single template key. */}
      <div className="mt-[6px] text-[13px] text-[#374151]">
        {S.progress(draft.answered_count)}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-[14px] gap-y-2">
        <button
          type="button"
          disabled={busy}
          onClick={resumeRun}
          className="min-h-[44px] px-[22px] rounded-[8px] border-none bg-[#2183FE] text-white text-[14px] font-semibold hover:bg-[#1668D9]"
        >
          {S.resume}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={discardRun}
          className="min-h-[44px] px-[10px] bg-transparent border-none text-[#6B7280] text-[13px] hover:text-[#111827]"
        >
          {S.discard}
        </button>
      </div>
    </div>
  ) : null;

  const emptyCard = (
    <div className="bg-[#E9F2FF] rounded-[8px] mt-4 py-[clamp(28px,5vw,52px)] px-[clamp(16px,3vw,32px)] flex flex-col items-center text-center">
      <div className="text-[17px] font-bold text-[#111827]">{S.emptyHint}</div>
      <div className="mt-[6px] max-w-[440px] text-[13px] leading-[1.5] text-[#374151] [text-wrap:pretty]">
        {S.emptySubline}
      </div>
      {!canStart && lockDate && (
        <div className="mt-[10px] text-[13px] text-[#6B7280]">
          {S.lockUntil(lockDate)}
        </div>
      )}
      <button
        type="button"
        disabled={!canStart || busy}
        onClick={beginRun}
        className={`mt-4 min-h-[44px] px-8 rounded-[8px] border-none bg-[#2183FE] text-white text-[14px] font-semibold ${
          !canStart || busy ? "opacity-45 cursor-default" : "hover:bg-[#1668D9]"
        }`}
      >
        {S.start}
      </button>
    </div>
  );

  return shell(
    <>
      {resumeCard}
      {result ? (
        <Evaluation
          result={result}
          lock={lock}
          evalIntro={state.eval_intro}
          history={history}
          catalogVersion={state.catalog_version}
          onReleaseChanged={load}
          onDeleted={load}
          onRetake={beginRun}
        />
      ) : (
        !draft && emptyCard
      )}
    </>
  );
};

export default CoachingPersonalitySection;
